import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Users,
  Plus,
  ArrowLeft,
  Calendar,
  Upload,
  FileText,
  Mic,
  Sparkles,
  CheckCircle,
  Clock,
  MapPin,
  Play,
  Download,
  Edit,
  Save,
  Loader2,
  Search,
  MessageSquare,
  ListChecks,
  Volume2
} from "lucide-react";

const MEETING_TYPES = [
  { value: "board", label: "Board Meeting" },
  { value: "annual", label: "Annual Meeting" },
  { value: "special", label: "Special Meeting" },
  { value: "drc", label: "DRC Meeting" },
  { value: "executive", label: "Executive Session" }
];

const MeetingsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  
  // Create meeting state
  const [createOpen, setCreateOpen] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    meeting_type: "",
    title: "",
    date: "",
    location: ""
  });
  
  // Meeting details state
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [attendeesInput, setAttendeesInput] = useState("");
  const [transcriptInput, setTranscriptInput] = useState("");
  const [generatingMinutes, setGeneratingMinutes] = useState(false);
  const [editingMinutes, setEditingMinutes] = useState(false);
  const [editedMinutes, setEditedMinutes] = useState("");
  
  // Transcription state
  const [transcribing, setTranscribing] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [transcriptionOpen, setTranscriptionOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Motion/Action item state
  const [addingMotion, setAddingMotion] = useState(false);
  const [motionForm, setMotionForm] = useState({
    description: "", moved_by: "", seconded_by: "", votes_for: 0, votes_against: 0, votes_abstain: 0
  });
  const [addingAction, setAddingAction] = useState(false);
  const [actionForm, setActionForm] = useState({
    description: "", assigned_to: "", due_date: ""
  });

  const fetchMeetings = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter && typeFilter !== "all") params.append("meeting_type", typeFilter);
      
      const response = await axios.get(`${API}/meetings?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data);
    } catch (error) {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, [token, statusFilter, typeFilter]);

  const handleCreateMeeting = async () => {
    if (!newMeeting.meeting_type || !newMeeting.title || !newMeeting.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/meetings`, newMeeting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Meeting created");
      setCreateOpen(false);
      setNewMeeting({ meeting_type: "", title: "", date: "", location: "" });
      fetchMeetings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    }
  };

  const handleUploadAgenda = async (file) => {
    if (!selectedMeeting || !file) return;
    
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/meetings/${selectedMeeting.meeting_id}/upload-agenda`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Agenda uploaded");
      refreshMeeting();
    } catch (error) {
      toast.error("Failed to upload agenda");
    }
  };

  const handleUploadRecording = async (file) => {
    if (!selectedMeeting || !file) return;
    
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/meetings/${selectedMeeting.meeting_id}/upload-recording`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Recording uploaded successfully");
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload recording");
    }
  };

  const handleTranscribe = async () => {
    if (!selectedMeeting?.recording) {
      toast.error("Please upload a recording first");
      return;
    }

    setTranscribing(true);
    try {
      const response = await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/transcribe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Transcription completed!");
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const handleGenerateAISummary = async () => {
    if (!selectedMeeting?.recording?.transcription_text) {
      toast.error("Please transcribe the recording first");
      return;
    }

    setGeneratingSummary(true);
    try {
      const response = await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/generate-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AI Summary generated!");
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Summary generation failed");
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleSearchTranscriptions = async () => {
    if (searchQuery.length < 3) {
      toast.error("Search query must be at least 3 characters");
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/meetings/transcriptions/search?query=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("No matches found");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleGenerateMinutes = async () => {
    if (!selectedMeeting || !attendeesInput) {
      toast.error("Please enter attendees");
      return;
    }

    setGeneratingMinutes(true);
    try {
      const formData = new FormData();
      formData.append("attendees", attendeesInput);
      if (transcriptInput) formData.append("transcript", transcriptInput);

      const response = await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/generate-minutes`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Minutes generated");
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate minutes");
    } finally {
      setGeneratingMinutes(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedMeeting) return;

    try {
      await axios.post(`${API}/meetings/${selectedMeeting.meeting_id}/generate-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Summary generated");
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate summary");
    }
  };

  const handleFinalizeMinutes = async () => {
    if (!selectedMeeting || !editedMinutes) {
      toast.error("Please edit and approve the minutes first");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("final_minutes", editedMinutes);

      await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/finalize-minutes`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Minutes finalized and saved to Document Library");
      setEditingMinutes(false);
      refreshMeeting();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to finalize minutes");
    }
  };

  const handleAddMotion = async () => {
    if (!selectedMeeting || !motionForm.description || !motionForm.moved_by || !motionForm.seconded_by) {
      toast.error("Please fill in all motion fields");
      return;
    }

    try {
      const formData = new FormData();
      Object.entries(motionForm).forEach(([key, value]) => formData.append(key, value));

      await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/add-motion`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Motion recorded");
      setAddingMotion(false);
      setMotionForm({ description: "", moved_by: "", seconded_by: "", votes_for: 0, votes_against: 0, votes_abstain: 0 });
      refreshMeeting();
    } catch (error) {
      toast.error("Failed to add motion");
    }
  };

  const handleAddAction = async () => {
    if (!selectedMeeting || !actionForm.description || !actionForm.assigned_to) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const formData = new FormData();
      Object.entries(actionForm).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      await axios.post(
        `${API}/meetings/${selectedMeeting.meeting_id}/add-action-item`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Action item added");
      setAddingAction(false);
      setActionForm({ description: "", assigned_to: "", due_date: "" });
      refreshMeeting();
    } catch (error) {
      toast.error("Failed to add action item");
    }
  };

  const refreshMeeting = async () => {
    if (!selectedMeeting) return;
    try {
      const response = await axios.get(`${API}/meetings/${selectedMeeting.meeting_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedMeeting(response.data);
      fetchMeetings();
    } catch (error) {
      console.error("Failed to refresh meeting");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: "bg-blue-500/10 text-blue-600",
      completed: "bg-green-500/10 text-green-600",
      minutes_pending: "bg-yellow-500/10 text-yellow-600",
      finalized: "bg-purple-500/10 text-purple-600"
    };
    return <Badge className={colors[status] || "bg-muted"}>{status?.replace("_", " ") || "scheduled"}</Badge>;
  };

  const content = (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page Title */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div>
          <span className="font-serif text-lg font-semibold block">Meetings & Minutes</span>
          <span className="text-xs text-muted-foreground">Schedule meetings and record minutes</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Upcoming</p>
                  <p className="text-2xl font-bold mt-1">{meetings.filter(m => m.status === "scheduled").length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-5 h-5 text-info" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Minutes Pending</p>
                  <p className="text-2xl font-bold text-warning mt-1">{meetings.filter(m => m.status === "minutes_pending").length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-5 h-5 text-warning" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Finalized</p>
                  <p className="text-2xl font-bold text-success mt-1">{meetings.filter(m => m.status === "finalized").length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="w-5 h-5 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Meetings</p>
                  <p className="text-2xl font-bold mt-1">{meetings.length}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {MEETING_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="minutes_pending">Minutes Pending</SelectItem>
              <SelectItem value="finalized">Finalized</SelectItem>
            </SelectContent>
          </Select>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="ml-auto shadow-button hover:shadow-button-hover transition-all duration-300">
                <Plus className="w-4 h-4 mr-2" />
                New Meeting
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Schedule New Meeting</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Meeting Type *</Label>
                  <Select value={newMeeting.meeting_type} onValueChange={(v) => setNewMeeting({...newMeeting, meeting_type: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEETING_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={newMeeting.title}
                    onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                    placeholder="e.g., January Board Meeting"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={newMeeting.date}
                    onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={newMeeting.location}
                    onChange={(e) => setNewMeeting({...newMeeting, location: e.target.value})}
                    placeholder="e.g., Community Center"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateMeeting}>Create Meeting</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Meetings List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No meetings found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {meetings.map((meeting) => (
              <Card key={meeting.meeting_id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{meeting.title}</h3>
                        {getStatusBadge(meeting.status)}
                        <Badge variant="outline">{MEETING_TYPES.find(t => t.value === meeting.meeting_type)?.label || meeting.meeting_type}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(meeting.date).toLocaleString()}
                        </span>
                        {meeting.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {meeting.location}
                          </span>
                        )}
                        {meeting.attendees?.length > 0 && (
                          <span>{meeting.attendees.length} attendees</span>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => { setSelectedMeeting(meeting); setDetailsOpen(true); setEditedMinutes(meeting.ai_minutes_draft || ""); }}>
                      Manage
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Meeting Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedMeeting?.title}</DialogTitle>
              <DialogDescription>
                {selectedMeeting && new Date(selectedMeeting.date).toLocaleString()}
                {selectedMeeting?.location && ` • ${selectedMeeting.location}`}
              </DialogDescription>
            </DialogHeader>
            
            {selectedMeeting && (
              <Tabs defaultValue="files">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="files">Files</TabsTrigger>
                  <TabsTrigger value="minutes">Minutes</TabsTrigger>
                  <TabsTrigger value="motions">Motions</TabsTrigger>
                  <TabsTrigger value="actions">Actions</TabsTrigger>
                </TabsList>
                
                <TabsContent value="files" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Agenda
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedMeeting.agenda_file_id ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm">Uploaded</span>
                          </div>
                        ) : (
                          <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => handleUploadAgenda(e.target.files[0])} />
                        )}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Mic className="w-4 h-4" />
                          Recording
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {selectedMeeting.recording ? (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm">{selectedMeeting.recording.filename}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{(selectedMeeting.recording.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                              {selectedMeeting.recording.duration && (
                                <span>• {Math.round(selectedMeeting.recording.duration / 60)} min</span>
                              )}
                            </div>
                          </div>
                        ) : selectedMeeting.recording_file_id ? (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm">Uploaded (legacy)</span>
                          </div>
                        ) : (
                          <Input type="file" accept="audio/*,video/*" onChange={(e) => handleUploadRecording(e.target.files[0])} />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Transcription Section */}
                  {(selectedMeeting.recording || selectedMeeting.recording_file_id) && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Volume2 className="w-4 h-4" />
                          AI Transcription & Summary
                        </CardTitle>
                        <CardDescription>
                          Automatically transcribe the recording and generate meeting insights
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Transcription Status */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Transcription:</span>
                            <Badge variant={
                              selectedMeeting.recording?.transcription_status === "completed" ? "success" :
                              selectedMeeting.recording?.transcription_status === "processing" ? "warning" :
                              selectedMeeting.recording?.transcription_status === "failed" ? "destructive" : "secondary"
                            }>
                              {selectedMeeting.recording?.transcription_status || "Not started"}
                            </Badge>
                          </div>
                          {selectedMeeting.recording?.transcription_status !== "completed" && (
                            <Button 
                              size="sm" 
                              onClick={handleTranscribe}
                              disabled={transcribing || selectedMeeting.recording?.transcription_status === "processing"}
                            >
                              {transcribing || selectedMeeting.recording?.transcription_status === "processing" ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Transcribing...
                                </>
                              ) : (
                                <>
                                  <Mic className="w-4 h-4 mr-2" />
                                  Start Transcription
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        
                        {/* Show transcription text if available */}
                        {selectedMeeting.recording?.transcription_text && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Transcription</Label>
                              <Button variant="ghost" size="sm" onClick={() => setTranscriptionOpen(!transcriptionOpen)}>
                                {transcriptionOpen ? "Collapse" : "Expand"}
                              </Button>
                            </div>
                            <div className={`bg-muted/50 rounded-lg p-3 text-sm ${transcriptionOpen ? "" : "max-h-32 overflow-hidden"}`}>
                              {selectedMeeting.recording.transcription_text}
                            </div>
                          </div>
                        )}
                        
                        {/* AI Summary Section */}
                        {selectedMeeting.recording?.transcription_status === "completed" && (
                          <div className="space-y-3 pt-3 border-t">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span className="text-sm font-medium">AI Summary</span>
                              </div>
                              {!selectedMeeting.ai_summary && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={handleGenerateAISummary}
                                  disabled={generatingSummary}
                                >
                                  {generatingSummary ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="w-4 h-4 mr-2" />
                                      Generate Summary
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                            
                            {selectedMeeting.ai_summary && (
                              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 text-sm">
                                {selectedMeeting.ai_summary}
                              </div>
                            )}
                            
                            {/* AI-extracted Action Items */}
                            {selectedMeeting.ai_action_items?.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <ListChecks className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium">AI-Extracted Action Items</span>
                                </div>
                                <div className="space-y-2">
                                  {selectedMeeting.ai_action_items.map((item, idx) => (
                                    <div key={idx} className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-2 text-sm">
                                      <p>{item.description}</p>
                                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                                        {item.assigned_to && <span>Assigned: {item.assigned_to}</span>}
                                        {item.due_date && <span>Due: {item.due_date}</span>}
                                        {item.priority && <Badge variant="outline" className="text-xs">{item.priority}</Badge>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
                
                <TabsContent value="minutes" className="space-y-4 mt-4">
                  {!selectedMeeting.ai_minutes_draft ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Attendees (comma-separated) *</Label>
                        <Input
                          value={attendeesInput}
                          onChange={(e) => setAttendeesInput(e.target.value)}
                          placeholder="John Smith, Jane Doe, Bob Wilson..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Transcript/Notes (optional)</Label>
                        <Textarea
                          value={transcriptInput}
                          onChange={(e) => setTranscriptInput(e.target.value)}
                          placeholder="Paste meeting transcript or notes..."
                          rows={6}
                        />
                      </div>
                      <Button onClick={handleGenerateMinutes} disabled={generatingMinutes}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {generatingMinutes ? "Generating..." : "Generate AI Minutes"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {editingMinutes ? (
                        <>
                          <Textarea
                            value={editedMinutes}
                            onChange={(e) => setEditedMinutes(e.target.value)}
                            rows={15}
                            className="font-mono text-sm"
                          />
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setEditingMinutes(false)}>Cancel</Button>
                            <Button onClick={handleFinalizeMinutes}>
                              <Save className="w-4 h-4 mr-2" />
                              Finalize & Publish
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="p-4 bg-muted rounded-lg max-h-[400px] overflow-y-auto">
                            <pre className="text-sm whitespace-pre-wrap">{selectedMeeting.ai_minutes_draft}</pre>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setEditingMinutes(true)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Minutes
                            </Button>
                            {!selectedMeeting.ai_summary && (
                              <Button variant="outline" onClick={handleGenerateSummary}>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Generate Summary
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                      
                      {selectedMeeting.ai_summary && (
                        <div className="p-4 border rounded-lg">
                          <Label className="mb-2 block">Meeting Summary</Label>
                          <pre className="text-sm whitespace-pre-wrap">{selectedMeeting.ai_summary}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="motions" className="space-y-4 mt-4">
                  {selectedMeeting.motions?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedMeeting.motions.map((motion, i) => (
                        <div key={i} className={`p-4 border rounded-lg ${motion.vote_result === 'passed' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                          <p className="font-medium">{motion.description}</p>
                          <div className="text-sm text-muted-foreground mt-2">
                            Moved by: {motion.moved_by} • Seconded by: {motion.seconded_by}
                          </div>
                          <div className="text-sm mt-1">
                            <Badge className={motion.vote_result === 'passed' ? 'bg-green-500' : 'bg-red-500'}>
                              {motion.vote_result?.toUpperCase()}
                            </Badge>
                            <span className="ml-2">({motion.votes_for} for, {motion.votes_against} against, {motion.votes_abstain} abstain)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No motions recorded</p>
                  )}
                  
                  {addingMotion ? (
                    <div className="p-4 border rounded-lg space-y-3">
                      <Label>Record Motion</Label>
                      <Textarea
                        placeholder="Motion description..."
                        value={motionForm.description}
                        onChange={(e) => setMotionForm({...motionForm, description: e.target.value})}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input placeholder="Moved by" value={motionForm.moved_by} onChange={(e) => setMotionForm({...motionForm, moved_by: e.target.value})} />
                        <Input placeholder="Seconded by" value={motionForm.seconded_by} onChange={(e) => setMotionForm({...motionForm, seconded_by: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs">For</Label>
                          <Input type="number" min="0" value={motionForm.votes_for} onChange={(e) => setMotionForm({...motionForm, votes_for: parseInt(e.target.value) || 0})} />
                        </div>
                        <div>
                          <Label className="text-xs">Against</Label>
                          <Input type="number" min="0" value={motionForm.votes_against} onChange={(e) => setMotionForm({...motionForm, votes_against: parseInt(e.target.value) || 0})} />
                        </div>
                        <div>
                          <Label className="text-xs">Abstain</Label>
                          <Input type="number" min="0" value={motionForm.votes_abstain} onChange={(e) => setMotionForm({...motionForm, votes_abstain: parseInt(e.target.value) || 0})} />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setAddingMotion(false)}>Cancel</Button>
                        <Button onClick={handleAddMotion}>Record Motion</Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setAddingMotion(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Motion
                    </Button>
                  )}
                </TabsContent>
                
                <TabsContent value="actions" className="space-y-4 mt-4">
                  {selectedMeeting.action_items?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedMeeting.action_items.map((item, i) => (
                        <div key={i} className="p-4 border rounded-lg flex items-start justify-between">
                          <div>
                            <p className="font-medium">{item.description}</p>
                            <p className="text-sm text-muted-foreground">Assigned to: {item.assigned_to}</p>
                            {item.due_date && <p className="text-sm text-muted-foreground">Due: {item.due_date}</p>}
                          </div>
                          <Badge variant="outline">{item.status || "pending"}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No action items</p>
                  )}
                  
                  {addingAction ? (
                    <div className="p-4 border rounded-lg space-y-3">
                      <Label>Add Action Item</Label>
                      <Textarea
                        placeholder="Action item description..."
                        value={actionForm.description}
                        onChange={(e) => setActionForm({...actionForm, description: e.target.value})}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input placeholder="Assigned to" value={actionForm.assigned_to} onChange={(e) => setActionForm({...actionForm, assigned_to: e.target.value})} />
                        <Input type="date" value={actionForm.due_date} onChange={(e) => setActionForm({...actionForm, due_date: e.target.value})} />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setAddingAction(false)}>Cancel</Button>
                        <Button onClick={handleAddAction}>Add Action Item</Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setAddingAction(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Action Item
                    </Button>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
    </div>
  );

  return <AdminSidebar>{content}</AdminSidebar>;
};

export default MeetingsPage;
